/**
 * PremisesSendMethodCard
 *
 * Premises-specific delivery UI for the Review & Send LOR page.
 * Displays the certified mail delivery method info and send status.
 */
import React from 'react';

import { formatMailingAddress } from '@/components/coverage/propertyContactAddress';
import type { PropertyContact } from '@/services/types/coverage-types';

interface PremisesSendMethodCardProps {
  selectedContact: PropertyContact | null;
  isPreparingCertifiedMail: boolean;
  isSendingCertifiedMail: boolean;
  certifiedMailSendStatus: string | null;
  emailHistorySection: React.ReactNode;
}

export const PremisesDeliveryMethodCard: React.FC = () => {
  return (
    <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-lg shadow-zinc-200/60">
      <div className="space-y-3">
        <div>
          <span className="text-sm font-semibold text-zinc-700">
            Delivery Method
          </span>
          <p className="text-xs text-zinc-500 mt-0.5">
            Premises property coverages are mailed by certified mail.
          </p>
        </div>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2">
          <p className="text-sm font-medium text-indigo-700">
            Certified Mail
          </p>
          <p className="text-xs text-indigo-600">
            The LOR will be sent through the Correspondence/PostGrid
            certified-mail workflow from this step.
          </p>
        </div>
      </div>
    </div>
  );
};

export const PremisesSendMethodCard: React.FC<PremisesSendMethodCardProps> = ({
  selectedContact,
  isPreparingCertifiedMail,
  isSendingCertifiedMail,
  certifiedMailSendStatus,
  emailHistorySection,
}) => {
  const statusMessage = (() => {
    if (isPreparingCertifiedMail || isSendingCertifiedMail) {
      return 'Preparing and queueing the certified-mail package...';
    }
    if (
      certifiedMailSendStatus === 'pending' ||
      certifiedMailSendStatus === 'sending'
    ) {
      return 'Certified mail has been queued. This step will complete after correspondence marks it sent.';
    }
    return 'Use Approve and Send above to queue this certified-mail package.';
  })();

  return (
    <div className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-lg shadow-zinc-200/60 space-y-4">
      <div>
        <span className="text-sm font-semibold text-zinc-700">
          Send via Certified Mail
        </span>
        <p className="text-xs text-zinc-500 mt-0.5">
          We will refresh the certified-mail package with the latest
          LOR, validate the recipient address, and send it through
          PostGrid when you click Approve and Send.
        </p>
      </div>
      {selectedContact && (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-1">
          <p className="text-xs font-medium text-zinc-700">
            Recipient
          </p>
          <p className="text-sm text-zinc-800">
            {selectedContact.name || 'Unnamed Contact'}
          </p>
          {(selectedContact.mailing_address ||
            selectedContact.address) && (
            <p className="text-xs text-zinc-600">
              {formatMailingAddress(selectedContact.mailing_address) ||
                selectedContact.address}
            </p>
          )}
        </div>
      )}
      {!selectedContact?.mailing_address && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs text-amber-800">
            No mailing address found for the selected contact. Please
            add an address in the Property Contacts tab before
            sending certified mail.
          </p>
        </div>
      )}
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
        <p className="text-xs text-zinc-600">{statusMessage}</p>
      </div>
      {emailHistorySection}
    </div>
  );
};
